/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dropdown, MenuProps } from "antd";
import React, { useState } from "react";
import { DropdownItemLink } from "../common/DropdownItemLink";
import { useSelector } from "react-redux";
import * as authSelectors from "../auth/authSelectors";
import { DropdownMenuWrapper } from "../common/DropdownMenuWrapper";
import { ellipsisStyle } from "../menus/components/MenuDropdown";
import { DropdownItemButton } from "../common/DropdownItemButton";
import { useUnlinkProductToShop } from "./service/shop-api";
import { ActionSpinner } from "../../components/ActionSpinner";
import { useFormContext } from "react-hook-form";
import { toastr } from "react-redux-toastr";
import { ProductListItem } from "../products/models/product";
import { getErrorMessage } from "../../error-handler";

type Props = {
  product: ProductListItem;
  editLink: string;
  shopId: string;
};

export const ShopProductsListDropdown = ({
  product,
  editLink,
  shopId,
}: Props) => {
  const [open, setOpen] = useState<boolean>(false);
  const { reset } = useFormContext();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const {
    mutateAsync: unlinkProductFromShop,
    isLoading: isUnlinkingProduct,
  } = useUnlinkProductToShop();

  const handleUnlink = async () => {
    try {
      const request = {
        organizationKeyName,
        productId: product.id,
        shopId,
      };
      await unlinkProductFromShop(request);
      toastr.success(
        "Success",
        "You successfully removed the product from the restaurant"
      );
      reset();
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    } finally {
      setOpen(false);
    }
  };

  const items: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <DropdownItemLink
          to={editLink}
          icon={<FontAwesomeIcon icon={solid("pen")} />}
        >
          Edit product
        </DropdownItemLink>
      ),
    },
    {
      key: "2",
      label: (
        <DropdownItemButton
          onClick={handleUnlink}
          icon={<FontAwesomeIcon icon={solid("trash")} />}
        >
          {isUnlinkingProduct ? <ActionSpinner /> : "Unlink from shop"}
        </DropdownItemButton>
      ),
    },
  ];

  const dropdownInnerWrapper = css`
    display: flex;
    flex-direction: column;
    padding-top: 8px;
    padding-left: 8px;
    padding-right: 8px;
    padding-bottom: 8px;
  `;

  return (
    <Dropdown
      menu={{
        items: items,
      }}
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      dropdownRender={(menu) => (
        <DropdownMenuWrapper>
          <div css={dropdownInnerWrapper}>
            {React.cloneElement(menu as React.ReactElement, {
              style: {
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                boxShadow: "none",
                padding: "0px",
                backgroundColor: "transparent",
              },
            })}
          </div>
        </DropdownMenuWrapper>
      )}
    >
      <div css={ellipsisStyle}>
        <FontAwesomeIcon icon={solid("ellipsis")} />
      </div>
    </Dropdown>
  );
};
